import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import FileTree from '../FileTree/FileTree';
import styles from './Folder.module.scss';
import { USER_TYPE } from '../../../../../../utils/constants/user';
import { FileNode } from '../../../../DataRoom';
import {
    dataRoomArrowUp,
    downloadIconDataRoom,
    downloadDarkGray,
    folderIconDataRoom,
    dataRoomDownloadIcon,
    downloadIconDataRoomHovered,
} from '../../../../../../assets/hostedassets';
import threeDotVertical from '../../../../../../assets/three-dot-vertical.svg';

import useFetchDataRoomFiles from '../../../../useFetchDataRoomFiles';
import { createPortal } from 'react-dom';
import { useAppSelector } from '../../../../../../app/hooks';
import { useSearchParams } from 'react-router-dom';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { getSubdomain } from '../../../../../../utils/utils';
import {
    AICA_DATAROOM_FILE_ARCHIVE,
    AICA_DATAROOM_FILE_DELETE,
    AICA_DATAROOM_FOLDER_VIEWED,
} from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { FILE_FOLDER_TYPES } from '../../../../../../store/investor/constants';
import { isLenderType } from '../../../../../../utils/utils';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import type { FeatureVisibility } from '../../../../../../utils/visibilityConfigUtils';
import { computeDropdownLeft, computeDropdownTop } from '../dropdownPositionUtils';
import { INGESTION_CHIP } from '../../../../constants';
import type { MissingStatementRange } from '../../../../types';

// "N/M Failed" counts over all nested files: M = files with a chip, N = Failed ones.
const countFailedRollup = (innerFiles: FileNode[]): { failedCount: number; totalCount: number } => {
    let failedCount = 0;
    let totalCount = 0;
    innerFiles.forEach((node) => {
        if (node.type === 'file') {
            const chip = node.metaData?.fileMetaData?.ingestionChip;
            if (chip) totalCount += 1;
            if (chip === INGESTION_CHIP.FAILED || chip === INGESTION_CHIP.UNSUPPORTED_FILE) failedCount += 1;
        } else if (node.innerFiles) {
            const nested = countFailedRollup(node.innerFiles);
            failedCount += nested.failedCount;
            totalCount += nested.totalCount;
        }
    });
    return { failedCount, totalCount };
};

interface FolderProps {
    name: string;
    innerFiles: FileNode[];
    lastUpdated: string;
    index: number;
    parentFolderName: string;
    fetchFiles: Function;
    setShowDataRoomModal: (value: { showModal: boolean; docId?: number; currentDocType?: string }) => void;
    isNew: any;
    selectedItems?: Set<string>;
    onItemSelect?: (itemId: string) => void;
    showCheckboxes?: boolean;
    isFolderFullySelected?: (folderId: string) => boolean;
    currentFileViewType?: string;
    dataRoomDownloadVisibility: FeatureVisibility;
    addToReportsVisible: boolean;
    onSendForSigning?: (docId: string, fileName: string) => void;
    signableExtensions?: string[];
    showSignedCopyVisibility?: FeatureVisibility;
    /** When true, a folder whose own rollup above shows a Failed child starts expanded
     *  (the "Review Now" deep link's `?expandFailed=1`). A folder with no Failed child
     *  stays collapsed regardless; when false/absent nothing auto-expands. */
    expandFailedAccounts?: boolean;
    /** This folder's own account identity, computed by the parent FileTree (see its
     *  `isBankAccountFolder`) — false on the Bank Statements root and on Reports; gates the
     *  N/M Failed rollup so it never renders on a non-account folder. */
    isBankAccountFolder?: boolean;
    /** This folder's own gap ranges; forwarded into its nested FileTree, which interleaves
     *  them among its file children. */
    missingStatements?: MissingStatementRange[];
}

const Folder: React.FC<FolderProps> = ({
    name,
    innerFiles,
    lastUpdated,
    index,
    fetchFiles,
    // isRecurCompany,
    parentFolderName,
    setShowDataRoomModal,
    isNew,
    selectedItems = new Set(),
    onItemSelect,
    showCheckboxes = false,
    isFolderFullySelected,
    currentFileViewType,
    dataRoomDownloadVisibility,
    addToReportsVisible,
    onSendForSigning,
    signableExtensions = [],
    showSignedCopyVisibility,
    expandFailedAccounts,
    isBankAccountFolder,
    missingStatements,
}) => {
    const { failedCount, totalCount } = useMemo(() => countFailedRollup(innerFiles), [innerFiles]);
    // Review Now deep link: start open when this folder has a Failed file.
    const [isOpen, setIsOpen] = useState(() => !!expandFailedAccounts && failedCount > 0);
    const [downloadImgUrl, setDownloadImgUrl] = useState(downloadIconDataRoomHovered);
    const { downloadTheFolder, downloading, handleDeleteFile, getFormattedDate, DeleteFolderButton, ArchiveFolderButton } =
        useFetchDataRoomFiles(fetchFiles);
    const [masterFolderName, setMasterFolderName] = useState('');
    const [showOtherOptions, setShowOtherOptions] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const threeDotsRef = useRef<HTMLSpanElement | null>(null);
    const fileViewType = searchParams.get('fileViewType');
    const isLender = isLenderType();
    const isArchiveView = fileViewType === 'archived';
    // MCA records are read-only sweep documents: no archiving from the UI, so they never leave the tab.
    const isMcaRecordsView = fileViewType === 'mcaRecords';
    let foldertoDelete = parentFolderName ? parentFolderName : name;
    const user = useAppSelector((state) => state.user.userData);
    const selectedCompany = useAppSelector((state) => state.investor.selectedCompany);
    const isPartner = user?.accountType === USER_TYPE.PARTNER;
    const isLenderOrPartner = isLender || isPartner;
    const folderId = name; // Use name as folder ID since folders don't have docId
    const isSelected = isFolderFullySelected ? isFolderFullySelected(folderId) : false;

    useEffect(() => {
        if (index === 1) {
            setMasterFolderName(name);
        } else {
            setMasterFolderName(parentFolderName ?? name);
        }
    }, [parentFolderName, index, name]);

    const calculateDropdownPosition = () => {
        if (!threeDotsRef.current) return;
        const rect = threeDotsRef.current.getBoundingClientRect();
        const dropdownRect = dropdownRef.current?.getBoundingClientRect();
        const dropdownHeight = dropdownRect?.height ?? 0;
        const dropdownWidth = dropdownRect?.width ?? 0;
        setDropdownPosition({
            top: computeDropdownTop(rect, dropdownHeight),
            left: computeDropdownLeft(rect, dropdownWidth),
        });
    };

    // useLayoutEffect (not useEffect): recalculates before the browser paints, so the
    // menu never flashes at its stale/initial position before snapping to the correct one.
    useLayoutEffect(() => {
        if (showOtherOptions) {
            calculateDropdownPosition();
        }
    }, [showOtherOptions]);

    useEffect(() => {
        const handleScroll = () => {
            if (showOtherOptions) {
                setShowOtherOptions(false);
            }
        };
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [showOtherOptions]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                threeDotsRef.current &&
                !threeDotsRef.current.contains(event.target as Node)
            ) {
                setShowOtherOptions(false);
            }
        };
        if (showOtherOptions) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showOtherOptions]);

    const dropdown =
        showOtherOptions &&
        createPortal(
            <div ref={dropdownRef} className={styles.OtherOptions} style={dropdownPosition}>
                {isArchiveView ? (
                    <>
                        <DeleteFolderButton
                            innerFiles={innerFiles}
                            name={name}
                            foldertoDelete={foldertoDelete}
                            parentFolderName={parentFolderName}
                            fetchFiles={fetchFiles}
                            setShowOtherOptions={setShowOtherOptions}
                            user={user}
                            selectedCompany={selectedCompany}
                            handleDeleteFile={handleDeleteFile}
                        />
                    </>
                ) : (
                    <>
                        <DeleteFolderButton
                            innerFiles={innerFiles}
                            name={name}
                            foldertoDelete={foldertoDelete}
                            parentFolderName={parentFolderName}
                            fetchFiles={fetchFiles}
                            setShowOtherOptions={setShowOtherOptions}
                            user={user}
                            selectedCompany={selectedCompany}
                            handleDeleteFile={handleDeleteFile}
                        />
                        {!isMcaRecordsView && (
                            <ArchiveFolderButton
                                innerFiles={innerFiles}
                                name={name}
                                foldertoDelete={foldertoDelete}
                                parentFolderName={parentFolderName}
                                fetchFiles={fetchFiles}
                                setShowOtherOptions={setShowOtherOptions}
                                user={user}
                                selectedCompany={selectedCompany}
                                handleDeleteFile={handleDeleteFile}
                            />
                        )}
                    </>
                )}
            </div>,
            document.body,
        );

    const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        if (onItemSelect) {
            onItemSelect(folderId);
        }
    };

    return (
        <div className={styles.folder}>
            <div
                className={isLenderOrPartner ? styles.folderHeaderLenderView : styles.folderHeader}
                onClick={() => {
                    if (!isOpen) {
                        Mixpanel.track(AICA_DATAROOM_FOLDER_VIEWED, {
                            userRole: user?.accountType,
                            pnoPlatform: getSubdomain(),
                            userId: user?.userId,
                            currentUrl: window.location.href,
                            organizationId: selectedCompany?.assesseeOrgId,
                            folderName: name,
                        });
                    }
                    setIsOpen(!isOpen);
                }}
            >
                <div className={styles.nameCell}>
                    <div
                        style={{
                            // -1: the row now carries its own 1.25rem left padding (added to align
                            // Status/Data From/Data Till/Last Updated with the header), which already
                            // accounts for one indent level — this offsets so top-level (index=1) rows
                            // land exactly under the header checkbox instead of double-indenting.
                            marginLeft: `${(index - 1) * 1.25}rem`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                        }}
                    >
                        <span>
                            <img className={isOpen ? styles.arrowDown : styles.arrowUp} src={dataRoomArrowUp} alt={'arrow'} />
                        </span>
                        {showCheckboxes && (
                            <div className={styles.checkboxCell} onClick={handleCheckboxClick}>
                                <input type="checkbox" checked={isSelected} onChange={handleCheckboxChange} className={styles.checkbox} />
                            </div>
                        )}
                        <span>
                            <img src={folderIconDataRoom} alt={'file'} />
                        </span>
                        <span className={styles.FolderName}>{name}</span>
                        {isNew && <span className={styles.NewBadge}>New</span>}
                    </div>
                </div>
                {/* Data Status cell — same grid track the ingestion chips render in one row down.
                    Renders on account folders (isBankAccountFolder, computed by the parent
                    FileTree) when M > 0 and >=1 child is Failed — the Bank Statements root and
                    Reports never show one,
                    matching the Figma frame; an all-successful account shows nothing. Persists
                    regardless of expand state — the Figma frame shows it on an expanded account
                    folder too, so it is not gated on isOpen. The span itself always renders
                    (even empty) so it stays this row's Data Status grid child. */}
                <span>
                    {isBankAccountFolder && failedCount > 0 && (
                        <span className={styles.FailedRollup}>
                            <span className={styles.FailedRollupDot} />
                            {failedCount}/{totalCount} Failed
                        </span>
                    )}
                </span>
                <span>{getFormattedDate('folder', '')}</span>
                <span>{getFormattedDate('folder', '')}</span>
                <span className={styles.FolderDate}>{lastUpdated}</span>
                <span />
                {!isLenderOrPartner && dataRoomDownloadVisibility.show && (
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                            if (dataRoomDownloadVisibility.disabled) return;
                            downloadTheFolder(innerFiles, name, false);
                        }}
                        className={styles.downloadIconContainer}
                        style={dataRoomDownloadVisibility.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                        onMouseEnter={() => !dataRoomDownloadVisibility.disabled && setDownloadImgUrl(downloadIconDataRoomHovered)}
                        onMouseLeave={() => !dataRoomDownloadVisibility.disabled && setDownloadImgUrl(downloadIconDataRoomHovered)}
                    >
                        {downloading ? <LoadingSpinner color="#6021B3" /> : <img src={downloadImgUrl} alt={'icon'} />}
                    </span>
                )}
                {/* {!isLenderOrPartner && <div className={styles.separator} />} */}
                {!isLenderOrPartner && (
                    <span
                        ref={threeDotsRef}
                        className={styles.ThreeDots}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowOtherOptions((prev) => !prev);
                        }}
                    >
                        <img src={threeDotVertical} style={{ cursor: 'pointer', width: '30px', height: '30px' }} alt={'More'} />
                    </span>
                )}
                {dropdown}
            </div>
            <div className={`${styles.folderContent} ${isOpen ? styles.open : styles.close}`}>
                <FileTree
                    data={innerFiles}
                    index={index}
                    masterFolderName={masterFolderName}
                    fetchFiles={fetchFiles}
                    setShowDataRoomModal={setShowDataRoomModal}
                    selectedItems={selectedItems}
                    onItemSelect={onItemSelect}
                    showCheckboxes={showCheckboxes}
                    isFolderFullySelected={isFolderFullySelected}
                    fileViewType={currentFileViewType}
                    dataRoomDownloadVisibility={dataRoomDownloadVisibility}
                    addToReportsVisible={addToReportsVisible}
                    onSendForSigning={onSendForSigning}
                    signableExtensions={signableExtensions}
                    showSignedCopyVisibility={showSignedCopyVisibility}
                    expandFailedAccounts={expandFailedAccounts}
                    missingStatements={missingStatements}
                />
            </div>
        </div>
    );
};

export default Folder;
